import { Spinner } from '@chakra-ui/react';
import { differenceInMinutes } from 'date-fns';

import { SFCheckIconCircleFilled, SFExclamationMarkTriangleOutlined } from '@assets/icons';
import { getPresignedUrl } from '@services/query-services/lambda.query-service';

const DEFAULT_BUCKET_NAME = import.meta.env.VITE_DEFAULT_BUCKET_NAME;

export const getUploadName = (upload) => {
  // If it's a meeting, use the title
  if (!upload || !Array.isArray(upload.results)) {
    return upload?.title || 'Untitled Meeting';
  }

  const originalFileEntry = getOriginalFileEntry(upload);
  if (!originalFileEntry) {
    return 'Unnamed File';
  }

  // Extract filename after the last slash
  const parts = originalFileEntry.split('/');
  let filename = parts[parts.length - 1];

  // Remove leading timestamp (digits followed by underscore)
  filename = filename.replace(/^\d+_/, '');

  return filename || 'Unnamed File';
};

export const findMatchingCard = (upload, cards, type) => {
  return cards?.find((c) => {
    if (type === 'Meeting') {
      return c.source_data.id === upload?.id;
    } else {
      return c.batch_id === upload.id;
    }
  });
};

export const getUploadLink = (upload, cards, type) => {
  if (type === 'Meeting') return `/meeting/${upload?.id}`;

  const card = findMatchingCard(upload, cards, type);
  if (card) return `/cards/${card.id}`;

  return null;
};

export const getDocumentUploadStatus = (upload, cards) => {
  const card = findMatchingCard(upload, cards, 'document');
  const isOldUpload = differenceInMinutes(new Date(), upload?.created_at) > 60;

  if (card) {
    return {
      icon: (
        <SFCheckIconCircleFilled
          color='brandPrimary.onContainer'
          boxSize={4}
        />
      ),
      text: 'Summarized'
    };
  } else if (isOldUpload) {
    return {
      icon: (
        <SFExclamationMarkTriangleOutlined
          color='brandPrimary.onContainer'
          boxSize={4}
        />
      ),
      text: 'Upload Failed'
    };
  } else {
    return {
      icon: (
        <Spinner
          size='sm'
          speed='0.8s'
          color='brandPrimary.primary'
          mr={1}
        />
      ),
      text: 'Summarizing'
    };
  }
};

export const getOriginalFileEntry = (upload) => {
  if (!upload || !Array.isArray(upload.results)) {
    return null;
  }

  return upload.results
    .find((r) => r.startsWith('original_file:'))
    ?.split('original_file:')
    ?.pop();
};

/**
 * Downloads a file from a batch object
 * @param {Object} batch - Batch object containing file information
 * @param {Function} setIsLoading - Function to set loading state
 * @returns {Promise<void>} - Promise that resolves when download is initiated
 * @throws {Error} - Throws error if download fails
 */
export const downloadFileFromBatch = async (batch, setIsLoading) => {
  if (!batch) {
    console.log('Batch object is required');
    return;
  }

  setIsLoading(true);
  try {
    const fileName = getOriginalFileEntry(batch);

    if (!fileName) {
      throw new Error('No original file found in batch');
    }

    // Get presigned URL from backend
    const { url } = await getPresignedUrl({
      command: 'get',
      bucket: DEFAULT_BUCKET_NAME,
      fileName
    });

    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (err) {
    console.error('Failed to download file:', err);
  } finally {
    setIsLoading(false);
  }
};

export const extractBatchIdFromTags = (card) => {
  if (!card?.tags || !Array.isArray(card.tags)) {
    return null;
  }

  const batchTag = card.tags.find((tag) => typeof tag === 'string' && tag.startsWith('batch_id:'));
  return batchTag ? batchTag.split('batch_id:')[1] : null;
};
